import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ArtworkCard from '../components/ArtworkCard';
import { useAuth } from '../context/AuthContext';

const ArtistProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [artist, setArtist] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useAuth();

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchArtistData();
  }, [id]);

  const fetchArtistData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/artist/${id}`);
      setArtist(response.data.artist);
      setArtworks(response.data.artworks);
    } catch (error) {
      console.error('Error fetching artist data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContactArtist = () => {
    if (!isAuthenticated) {
      alert('Please login to message artists');
      navigate('/login');
      return;
    }
    navigate(`/messages?recipient=${id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-orange-600"></div>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Artist not found</h2>
          <button onClick={() => navigate(-1)} className="btn-primary">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50">
      {/* Hero Section with Cover */}
      <div className="relative h-80 bg-gradient-to-r from-orange-600 via-red-600 to-amber-600 overflow-hidden">
        <div className="absolute inset-0 african-pattern opacity-20"></div>
        <div className="absolute inset-0 bg-black/20"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Artist Info Card */}
        <div className="relative -mt-32 mb-12">
          <div className="bg-white rounded-3xl shadow-african-lg p-8">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <div className="w-40 h-40 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center text-white text-6xl font-bold shadow-african ring-8 ring-white">
                  {artist.name?.charAt(0).toUpperCase()}
                </div>
                <div className="absolute bottom-2 right-2 w-8 h-8 bg-green-500 rounded-full border-4 border-white"></div>
              </div>

              {/* Info */}
              <div className="flex-1 text-center md:text-left">
                <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-2">
                  {artist.name}
                </h1>
                <p className="text-lg text-orange-600 font-semibold mb-4">
                  🎨 Professional Artist
                </p>
                <p className="text-gray-600 mb-6 max-w-2xl">
                  {artist.bio || 'A talented artist creating beautiful works that celebrate culture and creativity.'}
                </p>

                {/* Stats */}
                <div className="flex flex-wrap justify-center md:justify-start gap-6 mb-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
                      {artworks.length}
                    </div>
                    <div className="text-sm text-gray-600">Artworks</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
                      {artworks.reduce((sum, art) => sum + (art.views || 0), 0)}
                    </div>
                    <div className="text-sm text-gray-600">Total Views</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
                      {artworks.filter(art => art.inStock).length}
                    </div>
                    <div className="text-sm text-gray-600">Available</div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                  <button onClick={handleContactArtist} className="btn-primary">
                    <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                    Contact Artist
                  </button>
                  <button className="btn-outline">
                    <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    Share Profile
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* About Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <h2 className="text-3xl font-display font-bold text-gray-900 mb-6 african-accent inline-block">
            About the Artist
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            {artist.about || `${artist.name} is a passionate artist dedicated to creating unique and inspiring artworks. With a focus on African culture and contemporary themes, their work has captivated collectors worldwide.`}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl">
              <h3 className="font-bold text-gray-900 mb-2">Specialization</h3>
              <p className="text-gray-600">Contemporary African Art</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl">
              <h3 className="font-bold text-gray-900 mb-2">Location</h3>
              <p className="text-gray-600">Nigeria</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl">
              <h3 className="font-bold text-gray-900 mb-2">Member Since</h3>
              <p className="text-gray-600">
                {new Date(artist.createdAt).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long' 
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Artworks Section */}
        <div className="pb-12">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-display font-bold text-gray-900 african-accent inline-block">
              Artworks by {artist.name}
            </h2>
            <span className="text-gray-600">
              {artworks.length} {artworks.length === 1 ? 'piece' : 'pieces'}
            </span>
          </div>

          {artworks.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {artworks.map((artwork) => (
                <ArtworkCard key={artwork._id} artwork={artwork} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-white rounded-2xl shadow-lg">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-orange-200 to-red-200 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-display font-bold text-gray-900 mb-2">
                No artworks yet
              </h3>
              <p className="text-gray-600">
                This artist hasn't uploaded any artworks yet. Check back soon!
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArtistProfile;